/**
 * Off-main-thread twin of `parseManeuverTimeseriesValuesResponse` (see ./maneuverTimeseriesGroups.ts).
 *
 * The slim /maneuvers-timeseries-values parse is O(events × samples × channels) with a `round()` per
 * channel value; for a fleet selection (hundreds of events × ~100 samples × dozens of channels) that is
 * a multi-hundred-thousand-iteration synchronous stall at data-load time. This module fans that work out
 * one task PER EVENT across a @nodets pool.
 *
 * As with the VMG pilot, @nodets ships the task by source, so `parseManeuverEventTask` references nothing
 * from module scope — `round` and the foil-alias copy are inlined. `maneuverTimeseriesGroups.ts` stays
 * the source of truth + oracle; `__tests__/unit/maneuverTimeseriesParseTask.test.ts` pins parity.
 *
 * Lifecycle: `runParallel` (one-shot pool, auto-disposed) rather than a permanent pool — the parse is
 * infrequent (cached 15 min) so we do not keep ~hardwareConcurrency idle workers alive between loads.
 * The cache gates on input size (see maneuverTimeseriesCache.ts) so small parses stay inline and never
 * pay worker spawn cost.
 */
import { runParallel } from './parallel/poolManager';
import type {
  ManeuverTsPoint,
  ManeuverTimeseriesValuesEvent,
  ParseManeuverTimeseriesValuesResult,
} from './maneuverTimeseriesGroups';

/** Only offload when the payload is big enough that worker spawn pays for itself (else parse inline). */
export const MANEUVER_PARSE_OFFLOAD_MIN_SAMPLES = 4000;

const FOIL_OLD_NEW_CHANNEL_ALIASES: ReadonlyArray<readonly [string, string]> = [
  ['rud_imm_old_mm', 'rud_imm_lwd_mm'],
  ['rud_imm_new_mm', 'rud_imm_wwd_mm'],
  ['rh_old_mm', 'rh_lwd_mm'],
  ['rh_new_mm', 'rh_wwd_mm'],
  ['db_ext_old_mm', 'db_ext_lwd_mm'],
  ['db_ext_new_mm', 'db_ext_wwd_mm'],
  ['db_stow_old', 'db_stow_state_lwd'],
  ['db_stow_new', 'db_stow_state_wwd'],
  ['db_cant_old_deg', 'db_cant_lwd_deg'],
  ['db_cant_new_deg', 'db_cant_wwd_deg'],
  ['db_rake_old_deg', 'db_rake_lwd_deg'],
  ['db_rake_new_deg', 'db_rake_wwd_deg'],
  ['db_rake_aoa_old_deg', 'db_rake_aoa_lwd_deg'],
  ['db_rake_aoa_new_deg', 'db_rake_aoa_wwd_deg'],
];

/** Main-thread: append foil old/new aliases to the channel order (mirrors the private helper in the oracle). */
function addFoilOldNewAliasOrder(channelOrder: string[]): string[] {
  const result = [...channelOrder];
  const seen = new Set(result.map((channel) => channel.toLowerCase()));
  for (const [oldNewChannel, lwdWwdChannel] of FOIL_OLD_NEW_CHANNEL_ALIASES) {
    if (seen.has(lwdWwdChannel) && !seen.has(oldNewChannel)) {
      result.push(oldNewChannel);
      seen.add(oldNewChannel);
    }
  }
  return result;
}

/** Total sample count across events — the offload gate input. */
export function countManeuverValueSamples(rows: ManeuverTimeseriesValuesEvent[]): number {
  let n = 0;
  for (const row of rows) {n += row?.values?.length ?? 0;}
  return n;
}

/** Payload for one event's parse. `order` is the precomputed (lowercased + foil-alias) channel order. */
export interface ParseManeuverEventPayload {
  eventId: number;
  values: Array<Record<string, unknown>>;
  order: string[];
}

/**
 * Self-contained pool task: parse ONE event's value rows into chart points. MUST NOT reference anything
 * outside its own body (see file header) — `round` and the foil-alias copy are inlined on purpose.
 */
export function parseManeuverEventTask(payload: ParseManeuverEventPayload): ManeuverTsPoint[] {
  const { eventId, values, order } = payload;

  const round = (value: number | string, decimals: number): number => {
    try {
      const val = Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
      return Number.isNaN(val) ? 0 : val;
    } catch {
      return 0;
    }
  };

  const FOIL: ReadonlyArray<readonly [string, string]> = [
    ['rud_imm_old_mm', 'rud_imm_lwd_mm'],
    ['rud_imm_new_mm', 'rud_imm_wwd_mm'],
    ['rh_old_mm', 'rh_lwd_mm'],
    ['rh_new_mm', 'rh_wwd_mm'],
    ['db_ext_old_mm', 'db_ext_lwd_mm'],
    ['db_ext_new_mm', 'db_ext_wwd_mm'],
    ['db_stow_old', 'db_stow_state_lwd'],
    ['db_stow_new', 'db_stow_state_wwd'],
    ['db_cant_old_deg', 'db_cant_lwd_deg'],
    ['db_cant_new_deg', 'db_cant_wwd_deg'],
    ['db_rake_old_deg', 'db_rake_lwd_deg'],
    ['db_rake_new_deg', 'db_rake_wwd_deg'],
    ['db_rake_aoa_old_deg', 'db_rake_aoa_lwd_deg'],
    ['db_rake_aoa_new_deg', 'db_rake_aoa_wwd_deg'],
  ];
  const copyFoilOldNewAliases = (point: Record<string, unknown>): void => {
    for (const [oldNewChannel, lwdWwdChannel] of FOIL) {
      if (point[oldNewChannel] === undefined && point[lwdWwdChannel] !== undefined) {
        point[oldNewChannel] = point[lwdWwdChannel];
      }
    }
  };

  const points: ManeuverTsPoint[] = [];
  for (const valueRow of values ?? []) {
    const t = Number(valueRow.time);
    if (!Number.isFinite(t)) {continue;}
    const point: ManeuverTsPoint = {
      event_id: eventId,
      tws_bin: 0,
      tack: '',
      vmg_perc_avg: 0,
      race: '',
      source_name: '',
      time: t,
    };
    for (const ch of order) {
      const raw = valueRow[ch] ?? valueRow[ch.toLowerCase()] ?? valueRow[ch.toUpperCase()];
      if (raw !== undefined && raw !== null) {
        point[ch] = round(Number(raw), 2);
      }
    }
    copyFoilOldNewAliases(point as Record<string, unknown>);
    points.push(point);
  }
  return points;
}

/**
 * Parallel equivalent of `parseManeuverTimeseriesValuesResponse`: precompute the channel order once, then
 * parse each event's rows in parallel across the pool, and reassemble in `eventIds` order. Output is
 * identical to the synchronous version (guaranteed by the parity test).
 */
export async function parseManeuverTimeseriesValuesOffThread(
  rows: ManeuverTimeseriesValuesEvent[],
  eventIds: number[],
  channelOrder: string[]
): Promise<ParseManeuverTimeseriesValuesResult> {
  const order = addFoilOldNewAliasOrder(channelOrder.map((c) => c.toLowerCase()));
  const perRow = await runParallel(
    parseManeuverEventTask,
    rows.map((row) => ({
      eventId: Number(row.event_id),
      values: row.values ?? [],
      order,
    })),
    { batch: 'auto' }
  );

  const byEvent = new Map<number, ManeuverTsPoint[]>();
  rows.forEach((row, i) => {
    const id = Number(row.event_id);
    if (Number.isFinite(id)) {byEvent.set(id, perRow[i] ?? []);}
  });

  const dataArray: ManeuverTsPoint[][] = eventIds
    .map((id) => byEvent.get(id))
    .filter((series): series is ManeuverTsPoint[] => !!series && series.length > 0);

  return { dataArray, charts: [], channelOrder: order };
}
